import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDebouncedCallback } from 'use-debounce';
import { useAuth } from '../context/UseAuth';

// --- 1. IMPORTAR SERVICIOS REFACTORIZADOS Y NUEVOS ---
import { createPack, getAvailableEffects } from '../services/packs.service';
import {
  getEffectPreview,
  requestPresignedUrl,
  uploadFileDirectly,
  confirmUpload,
} from '../services/media.service';
// ----------------------------------------------------
import { generateVideoThumbnailFromFile } from '../utils/videoUtils';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-hot-toast';
import { useExchangeRates } from './useExchangeRates';

const getDefaultIntensity = (effectType, effects = {}) =>
  effects[effectType]?.defaultValue ?? 20;

const BASE_PRICE_USD = 10;

// --- 2. NUEVOS ESTADOS DE SUBIDA ---
const UPLOAD_STATE = {
  IDLE: 'idle',
  CREATING_DRAFT: 'creating_draft',
  UPLOADING_FILES: 'uploading_files',
  SUCCESS: 'success',
};
// ------------------------------------

export const useCreatePack = ({ onClose, onSuccess }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: rates, isLoading: isLoadingRates } = useExchangeRates('USD');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    isPublic: true,
    isPremium: true,
    commentsEnabled: true,
    price: { amount: BASE_PRICE_USD, currency: user?.currency || 'USD' },
  });

  // (Efecto para precio dinámico - sin cambios)
  useEffect(() => {
    if (!isLoadingRates && rates && user?.currency) {
      const userCurrency = user.currency;
      if (userCurrency === 'USD') {
        setFormData((prev) => ({
          ...prev,
          price: { amount: BASE_PRICE_USD, currency: 'USD' },
        }));
        return;
      }
      const conversionRate = rates[userCurrency];
      if (conversionRate) {
        const convertedAmount = Math.round(BASE_PRICE_USD * conversionRate);
        setFormData((prev) => ({
          ...prev,
          price: { amount: convertedAmount, currency: userCurrency },
        }));
      }
    }
  }, [isLoadingRates, rates, user?.currency]);

  const [items, setItems] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0); // <-- Este estado ya no se usará
  const [customCover, setCustomCover] = useState(null);
  const [error, setError] = useState(null);

  const [isEffectModalOpen, setIsEffectModalOpen] = useState(false);
  const [editingContext, setEditingContext] = useState(null);
  const [modalPreviewState, setModalPreviewState] = useState({
    url: null,
    intensity: 50,
    effectType: 'blur',
  });
  const [isPreviewing, setIsPreviewing] = useState(false);

  // --- 3. ESTADO DE SUBIDA REFACTORIZADO ---
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATE.IDLE);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusMessage, setUploadStatusMessage] = useState('');
  const isPending =
    uploadStatus !== UPLOAD_STATE.IDLE && uploadStatus !== UPLOAD_STATE.SUCCESS;
  // -----------------------------------------

  const [temporaryCoverPreviewUrl, setTemporaryCoverPreviewUrl] =
    useState(null);

  const { data: effectsData, isLoading: loadingEffects } = useQuery({
    queryKey: ['effects'],
    queryFn: getAvailableEffects,
    staleTime: Infinity,
  });
  const { mutate: previewMutation } = useMutation({
    mutationFn: (vars) =>
      getEffectPreview(
        vars.sourceFile,
        null, // sourceKey (no aplica para archivos nuevos)
        vars.effectType,
        vars.effectIntensity
      ),
    onSuccess: (blobUrl) => {
      setModalPreviewState((prev) => ({ ...prev, url: blobUrl }));
    },
    onError: () => toast.error('No se pudo generar la previsualización.'),
    onSettled: () => setIsPreviewing(false),
  });

  // Función debounced para solicitar la previsualización
  const debouncedRequestPreview = useDebouncedCallback(
    (file, effect, intensity) => {
      if (!file || !effect || effect === 'none') {
        setModalPreviewState((prev) => ({ ...prev, url: null }));
        setIsPreviewing(false);
        return;
      }
      setIsPreviewing(true);
      previewMutation({
        sourceFile: file,
        effectType: effect,
        effectIntensity: intensity,
      });
    },
    300
  );

  // --- 4. MUTACIÓN REFACTORIZADA: CREA BORRADOR Y DISPARA SUBIDAS ---
  const { mutate: createPackMutation } = useMutation({
    mutationFn: createPack, // Llama al servicio refactorizado (envía JSON)
    onSuccess: async (newPack) => {
      // -- INICIO DEL FLUJO DE SUBIDA (PASOS 2 Y 3) --
      try {
        const packId = newPack._id;
        const filesToUpload = [];

        // Añadir portada personalizada (si existe)
        if (customCover?.file) {
          filesToUpload.push({
            file: customCover.file,
            context: 'pack-cover',
            metadata: { effect: customCover.effect },
          });
        }

        // Añadir items del pack
        items.forEach((item) => {
          filesToUpload.push({
            file: item.file,
            context: 'packs', // 'packs' es el contexto para items de pack
            metadata: {
              effect: item.effect,
              title: item.file.name,
            },
          });
        });

        // Iniciar el proceso de subida secuencial
        setUploadStatus(UPLOAD_STATE.UPLOADING_FILES);

        for (let i = 0; i < filesToUpload.length; i++) {
          const { file, context, metadata } = filesToUpload[i];
          const fileNum = i + 1;

          setUploadStatusMessage(
            `Solicitando URL para archivo ${fileNum}/${filesToUpload.length}...`
          );
          setUploadProgress(0);

          const { presignedUrl, objectKey } = await requestPresignedUrl(
            file,
            context,
            packId
          );

          setUploadStatusMessage(
            `Subiendo archivo ${fileNum}/${filesToUpload.length}: ${file.name}...`
          );
          await uploadFileDirectly(presignedUrl, file, setUploadProgress);

          setUploadStatusMessage(
            `Confirmando archivo ${fileNum}/${filesToUpload.length}...`
          );
          await confirmUpload(objectKey, context, packId, metadata);
        }

        // -- FIN DEL FLUJO DE SUBIDA --
        setUploadStatus(UPLOAD_STATE.SUCCESS);
        setUploadStatusMessage('');
        toast.success(
          'Galería creada con éxito. Los archivos se están procesando.'
        );
        queryClient.invalidateQueries({
          queryKey: ['userPacks', user?.username],
        });
        if (onSuccess) onSuccess(newPack);
        onClose(); // Cierra el modal al finalizar
      } catch (uploadError) {
        setError(
          uploadError.message || 'Error durante la subida de un archivo.'
        );
        setUploadStatus(UPLOAD_STATE.IDLE);
        setUploadStatusMessage('');
      }
    },
    onError: (err) => {
      setError(err.response?.data?.message || 'Error al crear la galería.');
      setUploadStatus(UPLOAD_STATE.IDLE);
    },
  });
  // -----------------------------------------------------------------

  // (addFiles - sin cambios)
  const addFiles = useCallback(async (newFiles) => {
    const fileList = Array.from(newFiles);
    for (const file of fileList) {
      const newItem = {
        id: uuidv4(),
        file: file,
        thumbnailFile: null,
        effect: null,
        effectPreviewUrl: null,
      };
      if (file.type.startsWith('video/')) {
        try {
          newItem.thumbnailFile = await generateVideoThumbnailFromFile(file);
        } catch (err) {
          toast.error(
            `No se pudo generar la miniatura para ${file.name}.` + err.message
          );
        }
      }
      setItems((prev) => [...prev, newItem]);
    }
  }, []);

  // (removeFile - sin cambios)
  const removeFile = useCallback((idToRemove) => {
    setItems((prev) => prev.filter((item) => item.id !== idToRemove));
  }, []);

  // (setContentCover - sin cambios, aunque ya no se usa en handleSubmit)
  const setContentCover = useCallback((index) => {
    setCoverIndex(index);
  }, []);

  // (handleCoverFileChange - sin cambios)
  const handleCoverFileChange = useCallback((file) => {
    setTemporaryCoverPreviewUrl(null);
    setCustomCover({ file, effect: null });
  }, []);

  // (handleRemoveCover - sin cambios)
  const handleRemoveCover = useCallback(() => {
    setTemporaryCoverPreviewUrl(null);
    setCustomCover(null);
  }, []);

  // (Lógica del modal de efectos - sin cambios)
  const openEffectModal = useCallback(
    (context) => {
      setEditingContext(context);
      const defaultEffect = effectsData?.defaultEffect || 'blur';
      let currentEffect, currentIntensity, fileForPreview, initialPreviewUrl;

      if (context.type === 'item') {
        const { data: item } = context;
        if (!item) return;
        currentEffect = item.effect?.type || defaultEffect;
        currentIntensity =
          item.effect?.intensity ??
          getDefaultIntensity(currentEffect, effectsData?.effects);
        fileForPreview = item.thumbnailFile || item.file;
        initialPreviewUrl = item.effectPreviewUrl;
      } else if (context.type === 'customCover') {
        if (!customCover?.file) return;
        currentEffect = customCover.effect?.type || defaultEffect;
        currentIntensity =
          customCover.effect?.intensity ??
          getDefaultIntensity(currentEffect, effectsData?.effects);
        fileForPreview = customCover.file;
        initialPreviewUrl = temporaryCoverPreviewUrl;
      } else {
        return;
      }

      setModalPreviewState({
        url: initialPreviewUrl,
        effectType: currentEffect,
        intensity: currentIntensity,
      });
      if (!initialPreviewUrl) {
        debouncedRequestPreview(
          fileForPreview,
          currentEffect,
          currentIntensity
        );
      }
      setIsEffectModalOpen(true);
    },
    [
      customCover,
      effectsData,
      debouncedRequestPreview,
      temporaryCoverPreviewUrl,
    ]
  );
  const handleModalEffectSelect = (effectType) => {
    const intensity = getDefaultIntensity(effectType, effectsData?.effects);
    setModalPreviewState((prev) => ({
      ...prev,
      url: null,
      effectType,
      intensity,
    }));
    if (!editingContext) return;
    let fileForPreview;
    if (editingContext.type === 'item') {
      const { data: item } = editingContext;
      if (item) fileForPreview = item.thumbnailFile || item.file;
    } else if (editingContext.type === 'customCover') {
      fileForPreview = customCover?.file;
    }
    if (fileForPreview) {
      debouncedRequestPreview(fileForPreview, effectType, intensity);
    }
  };
  const handleModalIntensityChange = (intensity) => {
    if (!editingContext) return;
    setModalPreviewState((prev) => {
      let fileForPreview;
      if (editingContext.type === 'item') {
        const { data: item } = editingContext;
        if (item) fileForPreview = item.thumbnailFile || item.file;
      } else if (editingContext.type === 'customCover') {
        fileForPreview = customCover?.file;
      }
      if (fileForPreview && prev.effectType) {
        debouncedRequestPreview(fileForPreview, prev.effectType, intensity);
      }
      return { ...prev, intensity };
    });
  };
  const confirmItemEffect = useCallback(() => {
    const { effectType, intensity, url } = modalPreviewState;
    if (!editingContext) return;
    if (editingContext.type === 'item') {
      const targetId = editingContext.data.id;
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === targetId
            ? {
                ...item,
                effect: { type: effectType, intensity },
                effectPreviewUrl: url,
              }
            : item
        )
      );
    } else if (editingContext.type === 'customCover') {
      setCustomCover((prev) =>
        prev ? { ...prev, effect: { type: effectType, intensity } } : null
      );
      setTemporaryCoverPreviewUrl(url);
    }
    toast.success('Efecto preparado.');
    setIsEffectModalOpen(false);
    setEditingContext(null);
  }, [editingContext, modalPreviewState]);
  const getDisplayUrlForModal = () => {
    if (modalPreviewState.url) return modalPreviewState.url;
    if (!editingContext) return null;
    let fileForPreview;
    if (editingContext.type === 'item') {
      const { data: item } = editingContext;
      if (item) fileForPreview = item.thumbnailFile || item.file;
    } else if (editingContext.type === 'customCover') {
      fileForPreview = customCover?.file;
    }
    return fileForPreview ? URL.createObjectURL(fileForPreview) : null;
  };
  // ----------------------------------------------------

  // (Form field updaters - sin cambios)
  const updateField = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  }, []);
  const updatePrice = useCallback((price) => {
    setFormData((prev) => ({ ...prev, price }));
  }, []);
  // ----------------------------------------------------

  // --- 5. HANDLESUBMIT REFACTORIZADO: INICIA EL PASO 1 (CREAR BORRADOR) ---
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim())
      return setError(
        t('packs.create_modal.information_form.name_label_error')
      );
    if (items.length === 0 && !customCover?.file)
      return setError(t('packs.create_modal.file_upload.error_file_required'));

    setError(null);
    setUploadStatus(UPLOAD_STATE.CREATING_DRAFT);
    setUploadStatusMessage('Creando galería...');
    setUploadProgress(0);

    // Llama a la mutación con los metadatos JSON
    createPackMutation(formData);
  };
  // -----------------------------------------------------------------

  return {
    state: {
      formData,
      items,
      coverIndex, // (Se mantiene por si la UI lo usa, pero no es funcional)
      customCoverFile: customCover?.file,
      customCoverEffect: customCover?.effect,
      error,
      isPending,
      uploadProgress,
      uploadStatus, // <-- Exponer estado
      uploadStatusMessage, // <-- Exponer mensaje de estado
      UPLOAD_STATE, // <-- Exponer constantes
      loadingEffects,
      isEffectModalOpen,
      modalPreviewState,
      isPreviewing,
      effectsData: effectsData?.effects || {},
      temporaryCoverPreviewUrl,
    },
    handlers: {
      updateField,
      updatePrice,
      addFiles,
      removeFile,
      setContentCover,
      handleCoverFileChange,
      handleRemoveCover,
      openEffectModal,
      handleModalEffectSelect,
      handleModalIntensityChange,
      confirmItemEffect,
      setIsEffectModalOpen,
      getDisplayUrlForModal,
      handleSubmit,
    },
  };
};
